import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Crown, ArrowRight, Zap, Clock } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ExitIntentPopup = ({ user = null, subscription = null }) => {
    const [showPopup, setShowPopup] = useState(false);
    const navigate = useNavigate();

    const isPremium = subscription?.tier && subscription.tier !== 'free';

    const handleMouseLeave = useCallback((e) => {
        // Only trigger when mouse moves above viewport (exit intent)
        if (e.clientY <= 0 && !isPremium) {
            const dismissed = localStorage.getItem('ct_exit_intent_dismissed');
            if (dismissed) {
                const dismissedTime = parseInt(dismissed);
                // Don't show if dismissed within last 24 hours
                if (Date.now() - dismissedTime < 24 * 60 * 60 * 1000) return;
            }

            const alreadyShown = sessionStorage.getItem('ct_exit_intent_shown');
            if (!alreadyShown) {
                setShowPopup(true);
                sessionStorage.setItem('ct_exit_intent_shown', 'true');
            }
        }
    }, [isPremium]);

    useEffect(() => {
        // Only add for logged in free-tier users
        if (!user || isPremium) return;

        document.addEventListener('mouseleave', handleMouseLeave);
        return () => document.removeEventListener('mouseleave', handleMouseLeave);
    }, [user, isPremium, handleMouseLeave]);

    const handleDismiss = () => {
        setShowPopup(false);
        localStorage.setItem('ct_exit_intent_dismissed', Date.now().toString());
    };

    const handleUpgrade = () => {
        setShowPopup(false);
        navigate('/subscribe');
    };

    if (isPremium || !user) return null;

    return (
        <AnimatePresence>
            {showPopup && (
                <>
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[100]"
                        onClick={handleDismiss}
                    />

                    {/* Popup */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.9, y: 20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: 20 }}
                        className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-[101] w-[90%] max-w-md"
                    >
                        <div className="bg-[#0F131F] border border-emerald-500/20 rounded-3xl overflow-hidden shadow-2xl shadow-emerald-500/10">
                            {/* Close button */}
                            <button
                                onClick={handleDismiss}
                                className="absolute top-4 right-4 text-slate-500 hover:text-white transition-colors z-10"
                            >
                                <X className="w-5 h-5" />
                            </button>

                            {/* Gradient header */}
                            <div className="bg-gradient-to-br from-emerald-500/20 via-teal-500/10 to-transparent p-8 pb-6 text-center relative overflow-hidden">
                                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-emerald-500/10 rounded-full blur-[80px]" />
                                <div className="relative">
                                    <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-teal-500 rounded-2xl mx-auto mb-4 flex items-center justify-center shadow-xl shadow-emerald-500/30">
                                        <Crown className="w-8 h-8 text-white" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-white mb-2">Wait! Don't Leave Empty-Handed</h2>
                                    <p className="text-slate-400 text-sm">
                                        Your personalized roadmap is ready to unlock
                                    </p>
                                </div>
                            </div>

                            {/* Content */}
                            <div className="px-8 pb-8 pt-4">
                                {/* Offer */}
                                <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-2xl p-4 mb-6">
                                    <div className="flex items-center gap-2 mb-3">
                                        <Zap className="w-4 h-4 text-amber-400" />
                                        <span className="text-xs font-mono text-amber-400 uppercase">Limited Time Offer</span>
                                    </div>
                                    <ul className="space-y-2">
                                        {[
                                            'Unlimited AI Career Roadmaps',
                                            'Advanced Interview Prep',
                                            'Salary Insights & Negotiation',
                                            'Priority AI Coach Access',
                                        ].map((feature, i) => (
                                            <li key={i} className="flex items-center gap-2 text-sm text-slate-300">
                                                <div className="w-1.5 h-1.5 bg-emerald-400 rounded-full" />
                                                {feature}
                                            </li>
                                        ))}
                                    </ul>
                                </div>

                                {/* Price */}
                                <div className="text-center mb-6">
                                    <div className="flex items-center justify-center gap-2 mb-1">
                                        <span className="text-slate-500 line-through text-sm">₹999/mo</span>
                                        <span className="text-2xl font-bold text-white">₹499<span className="text-sm text-slate-400 font-normal">/mo</span></span>
                                    </div>
                                    <div className="flex items-center justify-center gap-1 text-xs text-amber-400">
                                        <Clock className="w-3 h-3" />
                                        <span>Offer expires soon</span>
                                    </div>
                                </div>

                                {/* CTA */}
                                <button
                                    onClick={handleUpgrade}
                                    className="w-full py-3.5 bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-bold rounded-xl flex items-center justify-center gap-2 hover:shadow-lg hover:shadow-emerald-500/30 transition-all"
                                >
                                    <span>Unlock Premium Now</span>
                                    <ArrowRight className="w-4 h-4" />
                                </button>

                                <button
                                    onClick={handleDismiss}
                                    className="w-full mt-3 py-2 text-slate-500 text-xs hover:text-slate-300 transition-colors"
                                >
                                    No thanks, I'll stay on the free plan
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </>
            )}
        </AnimatePresence>
    );
};

export default ExitIntentPopup;
